import * as Tooltip from "@radix-ui/react-tooltip";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  PublicUserOrganizationFragment,
  ResourceViewFragment,
} from "../../__generated__/graphql";
import { useMemo } from "react";
import { FaEye } from "react-icons/fa6";

interface ResourceViewsComponentProps {
  participants?: PublicUserOrganizationFragment[];
  resourceViews: ResourceViewFragment[];
  isRessourcePublic?: boolean;
}

export default function ResourceViewsComponent({
  participants,
  resourceViews,
  isRessourcePublic = false,
}: ResourceViewsComponentProps) {
  // Create a map of participantId -> view for quick lookup
  const viewsByParticipantId = useMemo(() => {
    const map = new Map<string, { viewedAt: string | Date }>();
    resourceViews.forEach((view) => {
      if (view?.userOrganization?.id) {
        map.set(view.userOrganization.id, {
          viewedAt: view.viewedAt,
        });
      }
    });
    return map;
  }, [resourceViews]);

  // For public resources, get all viewers from resourceViews
  // For non-public resources, count participants who have viewed
  const { viewedCount, totalParticipants } = useMemo(() => {
    if (isRessourcePublic) {
      // Public: get all unique viewers from resourceViews
      const viewers = resourceViews
        .map((view) => view?.userOrganization)
        .filter((user): user is NonNullable<typeof user> => !!user);
      return {
        viewedCount: viewers.length,
        totalParticipants: 0, // Not relevant for public resources
      };
    } else {
      // Non-public: compare participants with views
      const viewedCount = participants?.filter((participant) => {
        const participantId = participant.id;
        return viewsByParticipantId.has(participantId);
      }).length;
      return {
        viewedCount: viewedCount ?? 0,
        totalParticipants: participants?.length ?? 0,
      };
    }
  }, [participants, viewsByParticipantId, resourceViews, isRessourcePublic]);

  const hasViewedAll =
    !isRessourcePublic && viewedCount > 0 && viewedCount === totalParticipants;

  // Get display message
  const getMessage = () => {
    if (isRessourcePublic) {
      // Public: just show count, no "Aucun" or "Tous"
      if (viewedCount === 0) {
        return "0 personne a consulté cette ressource";
      }
      return `${viewedCount} personne${viewedCount > 1 ? "s" : ""} ${
        viewedCount > 1 ? "ont" : "a"
      } consulté cette ressource`;
    } else {
      // Non-public: original behavior
      if (viewedCount === 0) {
        return "Aucun participant n'a consulté cette ressource";
      }
      if (hasViewedAll) {
        return "Tous les participants ont consulté cette ressource";
      }
      return `${viewedCount} participant${viewedCount > 1 ? "s" : ""} ${
        viewedCount > 1 ? "ont" : "a"
      } consulté cette ressource`;
    }
  };

  // Format date for display
  const formatViewDate = (date: string | Date) => {
    try {
      return format(new Date(date), "d MMMM yyyy à HH:mm", {
        locale: fr,
      });
    } catch {
      return String(date);
    }
  };

  // If there are no participants (and not public), don't show the tooltip
  if (!isRessourcePublic && totalParticipants === 0) {
    return (
      <div className="flex flex-row items-center gap-2 text-accent w-fit">
        <FaEye className="w-3 h-3" />
        <div className="text-xs">{getMessage()}</div>
      </div>
    );
  }

  // If public and no viewers, don't show the tooltip
  if (isRessourcePublic && viewedCount === 0) {
    return (
      <div className="flex flex-row items-center gap-2 text-accent w-fit">
        <FaEye className="w-3 h-3" />
        <div className="text-xs">{getMessage()}</div>
      </div>
    );
  }

  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger asChild>
          <div className="flex flex-row items-center gap-2 text-accent w-fit">
            <FaEye className="w-3 h-3" />
            <div className="text-xs cursor-help">{getMessage()}</div>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="rounded-md bg-card border border-border px-4 py-3 text-sm text-white max-w-md z-100"
            sideOffset={5}
          >
            <div className="flex flex-col gap-2">
              {isRessourcePublic
                ? // Public: only show viewers from resourceViews
                  resourceViews.map((view, index) => {
                    if (!view?.userOrganization) return null;

                    const viewer = view.userOrganization;
                    const viewerId = viewer.id;
                    const viewerName = viewer.name;

                    return (
                      <div
                        key={viewerId || index}
                        className="flex items-center gap-2"
                      >
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        <div className="flex-1">
                          <div className="font-medium">{viewerName}</div>
                          <div className="text-xs text-gray-300">
                            Consulté le {formatViewDate(view.viewedAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                : // Non-public: show all participants with view status
                  participants?.map((participant, index) => {
                    if (!participant?.id) return null;

                    const participantId = participant.id;
                    const view = viewsByParticipantId.get(participantId);
                    const hasViewed = !!view;
                    const participantName = participant.name;

                    return (
                      <div
                        key={participantId || index}
                        className="flex items-center gap-2"
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${
                            hasViewed ? "bg-green-500" : "bg-gray-400"
                          }`}
                        />
                        <div className="flex-1">
                          <div className="text-xs font-medium">
                            {participantName}
                          </div>
                          {hasViewed && view && (
                            <div className="text-xs text-secondary">
                              Consulté le {formatViewDate(view.viewedAt)}
                            </div>
                          )}
                          {!hasViewed && (
                            <div className="text-xs text-tertiary">
                              Non consulté
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
            </div>
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
